import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAppDispatch } from "../../../redux/store"
import { sendThankYouMessage } from "../../../redux/slices/events/eventsSlice"
import { setBalance } from "../../../redux/slices/auth/authSlice"
import Drawer from "./Drawer"
import styles from "../EventDashboardV2.module.scss"

const MAX_MESSAGE_LENGTH = 255

const SpinnerIcon = () => (
  <svg className={styles.spinIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
)

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

const BigCheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="40" height="40">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
)

interface ThankYouComposeDrawerProps {
  show: boolean
  onClose: () => void
  eligibleIds: number[]
  totalSelected: number
  onSuccess: (sent: number, skipped: number) => void
  notify: (text: string, isError: boolean) => void
}

const ThankYouComposeDrawer: React.FC<ThankYouComposeDrawerProps> = ({
  show,
  onClose,
  eligibleIds,
  totalSelected,
  onSuccess,
  notify,
}) => {
  const { t } = useTranslation()
  const { event_id } = useParams()
  const dispatch = useAppDispatch()

  const [message, setMessage] = useState("")
  // Drawer local flow: idle → sending → sent. On `sent`, we briefly show an
  // in-drawer confirmation checkmark so the user gets visible closure before
  // the drawer animates closed. `onSuccess` is deferred until that moment.
  const [phase, setPhase] = useState<"idle" | "sending" | "sent">("idle")
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!show) {
      setMessage("")
      setPhase("idle")
      setTouched(false)
    }
  }, [show])

  const sending = phase === "sending"
  const sent = phase === "sent"

  const hasError = touched && message.trim().length === 0
  const isOverLimit = message.length > MAX_MESSAGE_LENGTH
  const skippedCount = totalSelected - eligibleIds.length

  const handleSend = () => {
    setTouched(true)
    if (message.trim().length === 0 || isOverLimit) return

    setPhase("sending")
    dispatch(
      sendThankYouMessage({
        event_contact_ids: eligibleIds,
        event_id: Number(event_id),
        message_text: message.trim(),
      }),
    )
      .then((result: any) => {
        if (result.error || result.meta?.rejectedWithValue) {
          const msg = typeof result.payload === "string"
            ? result.payload
            : t("eventDashboardV2.messages.failedSendThankYou")
          notify(msg, true)
          setPhase("idle")
        } else {
          if (result.payload?.balance !== undefined) {
            dispatch(setBalance(result.payload.balance))
          }
          // Show an in-drawer success state so the click produces a clearly
          // visible acknowledgement, then dismiss and hand off to the parent
          // banner. ~1.1s is long enough to read, short enough to not feel slow.
          setPhase("sent")
          window.setTimeout(() => {
            onSuccess(eligibleIds.length, skippedCount)
          }, 1100)
        }
      })
      .catch(() => {
        notify(t("eventDashboardV2.messages.somethingWentWrong"), true)
        setPhase("idle")
      })
  }

  const footer = sent ? null : (
    <div className={styles.drawerFooterActions}>
      <button
        type="button"
        className={styles.drawerBtnCancel}
        onClick={onClose}
        disabled={sending}
      >
        {t("eventDashboardV2.actions.cancel")}
      </button>
      <button
        type="button"
        className={styles.drawerBtnSave}
        onClick={handleSend}
        disabled={sending || isOverLimit}
      >
        {sending ? (
          <>
            <SpinnerIcon /> {t("eventDashboardV2.actions.sending")}
          </>
        ) : (
          t("eventDashboardV2.messages.sendToGuests", { count: eligibleIds.length })
        )}
      </button>
    </div>
  )

  return (
    <Drawer
      open={show}
      onClose={sending || sent ? () => {} : onClose}
      title={t("eventDashboardV2.actions.sendThankYouMessage")}
      footer={footer}
    >
      {sent ? (
        <div className={styles.composeSuccessState}>
          <div className={styles.composeSuccessIcon}>
            <BigCheckIcon />
          </div>
          <h3 className={styles.composeSuccessTitle}>
            {t("eventDashboardV2.messages.thankYouSent", { count: eligibleIds.length })}
          </h3>
          <p className={styles.composeSuccessSub}>
            {t("eventDashboardV2.messages.thankYouSuccessSub")}
          </p>
        </div>
      ) : (
        <>
          <div className={styles.composeRecipientCard}>
            <div className={styles.composeRecipientRow}>
              <UsersIcon />
              <span className={styles.composeRecipientCount}>
                {t("eventDashboardV2.messages.guestsWillReceiveMessage", { count: eligibleIds.length })}
              </span>
            </div>
            {skippedCount > 0 && (
              <p className={styles.composeRecipientSkipped}>
                {t("eventDashboardV2.messages.skippedNoPhone", { count: skippedCount })}
                {/*
                  With explicit selection, the only remaining skip reason
                  is "no phone number" — re-sending to previously-thanked
                  guests is allowed when deliberately selected. Without
                  selection the bulk default also skips already-thanked
                  guests, but the drawer isn't shown in that zero-skip
                  case often enough to justify branching the copy.
                */}
              </p>
            )}
          </div>

          <div className={styles.composeField}>
            <label className={styles.drawerFieldLabel}>
              {t("eventDashboardV2.labels.message")} <span className={styles.composeRequired}>*</span>
            </label>
            <textarea
              className={`${styles.composeTextarea} ${hasError ? styles.drawerInputError : ""}`}
              rows={6}
              placeholder={t("eventDashboardV2.messages.thankYouMessagePlaceholder")}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value)
                if (!touched) setTouched(true)
              }}
              disabled={sending}
              maxLength={MAX_MESSAGE_LENGTH + 10}
            />
            <div className={styles.composeFooterRow}>
              {hasError && (
                <span className={styles.drawerFieldError}>
                  {t("eventDashboardV2.messages.thankYouMessageRequired")}
                </span>
              )}
              <span
                className={`${styles.composeCharCount} ${isOverLimit ? styles.composeCharCountOver : ""}`}
              >
                {message.length}/{MAX_MESSAGE_LENGTH}
              </span>
            </div>
          </div>

          <p className={styles.composeHint}>
            {t("eventDashboardV2.messages.thankYouHint")}
          </p>

          {sending && (
            <div className={styles.composeSendingStrip} role="status">
              <SpinnerIcon />
              <span>
                {t("eventDashboardV2.messages.queueingThankYou", { count: eligibleIds.length })}
              </span>
            </div>
          )}
        </>
      )}
    </Drawer>
  )
}

export default ThankYouComposeDrawer
